import {
  getMessageFromNetworkError,
  Template,
  useDeleteTemplate,
} from "@zapier/client";

import {
  Button,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Text,
} from "@zapier/design-system";

import { useTranslations } from "@zapier/i18n";
import { FlexSpacer } from "@zapier/shared";
import { useNotification } from "@zapier/utils";

interface TemplateDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: Template | undefined;
}

/**
 * Confirmation modal for deleting a template. Calls useDeleteTemplate on confirm;
 * on success shows notification and closes (list updates via invalidation). On
 * error shows notification and keeps modal open (AC7).
 */
export function TemplateDeleteModal({
  isOpen,
  onClose,
  template,
}: TemplateDeleteModalProps) {
  const t = useTranslations("template-delete-modal");
  const { createNotification } = useNotification();
  const { mutate: deleteTemplate, isPending } = useDeleteTemplate();

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  if (!template) {
    return;
  }

  const handleConfirm = () => {
    deleteTemplate(
      { id: template.id },
      {
        onSuccess: () => {
          createNotification({
            message: (
              <Text>
                {t("notifications.template-deleted-success", {
                  templateName: template.name,
                })}
              </Text>
            ),
            type: "success",
          });
          onClose();
        },
        onError: (error) => {
          createNotification({
            title: t("notifications.unable-to-delete"),
            message: getMessageFromNetworkError(error),
            type: "error",
          });
        },
      },
    );
  };

  return (
    <ModalDialogRoot isOpen={isOpen} onOpenChange={handleOpenChange}>
      <ModalDialogHeader>
        {t("header.title", { templateName: template.name })}
      </ModalDialogHeader>
      <ModalDialogBody>
        <Text color="TextWeaker">{t("messages.confirm-delete")}</Text>
      </ModalDialogBody>
      <ModalDialogFooter>
        <Button
          type="button"
          isDisabled={isPending}
          variant="tertiary"
          onPress={onClose}
        >
          {t("buttons.cancel")}
        </Button>
        <FlexSpacer />
        <Button
          isDisabled={isPending}
          isPending={isPending}
          variant="danger"
          onPress={handleConfirm}
        >
          {t("buttons.delete")}
        </Button>
      </ModalDialogFooter>
    </ModalDialogRoot>
  );
}

export default TemplateDeleteModal;
